import { ActionIcon, Box, Button, CloseButton, Flex, Group, Stack, Text } from '@mantine/core';
import { IconCaretDown, IconCaretUp, IconUser } from '@tabler/icons-react';
import { ReactNode } from 'react';
import { FieldValues, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormControlInput, TextInputControl } from '@/core/components';

import { useCurrentUser } from '@feature/security';

export interface AuthorListControlProps<T extends FieldValues> extends FormControlInput<T> {
  withAsterisk?: boolean;

  label: string;

  description?: string;
  isDisabled?: boolean;

  renderMembership: (v: { membershipName: string }) => ReactNode;
}

const defaultAuthor = {
  alias: '',
  userName: '',
};

export function AuthorListControl<T extends FieldValues>(props: AuthorListControlProps<T>) {
  const { label, description, name, isSubmitting, isDisabled, renderMembership, control } = props;
  const { fields, append, remove, move } = useFieldArray({
    /* @ts-expect-error fix later */
    name,
    keyName: 'uuid',
    control,
  });
  const { t } = useTranslation();

  const { email: alias, lastName, firstName } = useCurrentUser();
  const currentAuthor = { alias, userName: `${lastName} ${firstName}` };

  return (
    <Box>
      <Flex justify="space-between">
        <Box>
          <Text
            fw="500"
            size="xl"
          >
            {label}
          </Text>
          {description && <Text c="dimmed">{description}</Text>}
        </Box>
        <Group>
          <Button
            size="sm"
            variant="outline"
            disabled={isDisabled}
            // @ts-expect-error fix in the future
            onClick={() => append(currentAuthor)}
          >
            {t('button.addCurrentAuthor')}
          </Button>
          <Button
            size="sm"
            disabled={isDisabled}
            // @ts-expect-error fix in the future
            onClick={() => append(defaultAuthor)}
          >
            {t('button.add')}
          </Button>
        </Group>
      </Flex>
      <Stack gap="xl">
        {fields.length === 0 && (
          <Text
            size="xs"
            c="dimmed"
            fs="italic"
          >
            {t('authors.emptyList')}
          </Text>
        )}
        {fields.map((field, index) => (
          <Flex
            align="flex-end"
            justify="center"
            gap="4px"
            key={field.uuid}
          >
            <Text
              mr="8px"
              mt="25px"
              size="sm"
            >
              #{index + 1}
            </Text>
            <Stack
              mt="12px"
              mr="8px"
              gap="2px"
            >
              <ActionIcon
                size={22}
                variant="outline"
                aria-label="sus"
                disabled={index === 0}
              >
                <IconCaretUp
                  onClick={() => move(index, Math.max(index - 1, 0))}
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
              <ActionIcon
                size={22}
                variant="outline"
                aria-label="jos"
                disabled={index === fields.length - 1}
              >
                <IconCaretDown
                  onClick={() => move(index, Math.min(index + 1, fields.length - 1))}
                  style={{ width: '70%', height: '70%' }}
                  stroke={1.5}
                />
              </ActionIcon>
            </Stack>
            {/* @ts-expect-error fix later */}
            <TextInputControl
              leftSection={
                <IconUser
                  stroke={2}
                  size={14}
                />
              }
              label={t('authors.form.fields.alias')}
              description={t('authors.form.fields.aliasDescription')}
              name={`${name}.${index}.alias`}
              control={control}
              isSubmitting={isSubmitting}
              isDisabled={isDisabled}
            />
            {/* @ts-expect-error fix later */}
            <TextInputControl
              label={t('authors.form.fields.userName')}
              control={control}
              name={`${name}.${index}.userName`}
              isSubmitting={isSubmitting}
              isDisabled={isDisabled}
            />
            {renderMembership({ membershipName: `${name}.${index}.membership` })}
            <CloseButton
              mt="25px"
              onClick={() => remove(index)}
              c="red"
            />
          </Flex>
        ))}
      </Stack>
    </Box>
  );
}
